import React from 'react';

import PropTypes from 'prop-types';

import { LinkIcon } from 'reaxl';
import { EmailCTA } from 'reaxl-email';

/**
 * VhrLink - A reusable component for rendering Vehicle History Report (VHR) links
 *
 * This component handles the rendering of VHR links from various providers (CARFAX, Experian/AutoCheck).
 * It can render either a regular link (LinkIcon) or an email lead gate (EmailCTA) depending on the
 * isLeadGateEnabled flag. The component supports both inverse styling and conditional hiding of
 * CARFAX tiles based on experiment flags.
 *
 * @param {Object} props - Component props
 * @param {string} [props.className] - Additional CSS class names to apply
 * @param {boolean} [props.isCarfaxTile] - Whether this is a CARFAX tile (affects conditional rendering)
 * @param {boolean} [props.isInverse=false] - Whether to use inverse styling (light text on dark background)
 * @param {boolean} [props.isLeadGateEnabled] - Whether to show email lead gate instead of direct link
 * @param {string} [props.labelKey] - Email CTA label key for lead gate rendering
 * @param {string} [props.linkText] - Text to display in the link
 * @param {Function} [props.onClick] - Click handler for the link
 * @param {Function} [props.onEmailCtaClick] - Click handler for email CTA lead gate
 * @param {boolean} [props.removeVhrCarfaxTile] - Whether to remove CARFAX tile props (experiment flag)
 * @param {Object} props.vhrTile - VHR tile data containing link information
 * @param {Object} props.vhrTile.link - Link object with href and other properties
 * @param {string} props.vhrTile.link.href - The URL for the VHR link
 *
 * @returns {JSX.Element|null} Rendered VHR link component or null if no link data
 */
export default function VhrLink({
    className,
    isCarfaxTile,
    isInverse = false,
    isLeadGateEnabled,
    labelKey,
    linkText,
    onClick: handleClick,
    onEmailCtaClick,
    removeVhrCarfaxTile,
    vhrTile,
    listingId,
}) {
    // eslint-disable-next-line no-unused-vars
    const { label, offsite, ...vhrLinkIconProps } = vhrTile?.link || {};
    const vhrProps = isCarfaxTile && removeVhrCarfaxTile ? {} : vhrLinkIconProps;
    const isExperianHref = !!vhrProps?.href?.includes('/cars-for-sale/experian');
    const shouldRedirectToExperianPage = isExperianHref && !!listingId;
    const href = shouldRedirectToExperianPage
        ? vhrProps?.href?.replace('/cars-for-sale/', `/cars-for-sale/vehicle/${listingId}/`)
        : vhrProps?.href;
    const target = shouldRedirectToExperianPage ? '_self' : '_blank';

    if (isLeadGateEnabled) {
        return (
            <EmailCTA
                className={className}
                context="vehicleHistoryReport"
                labelKey={labelKey}
                onClick={onEmailCtaClick}
                type={isInverse ? 'link-decorated-inverse' : 'link-decorated'}
            />
        );
    }
    return (
        <LinkIcon
            {...vhrProps}
            {...(href && { href })}
            className={className}
            inverse={isInverse}
            onClick={handleClick}
            rel="nofollow noopener"
            target={target}
        >
            {linkText}
        </LinkIcon>
    );
}

VhrLink.propTypes = {
    /** Additional CSS class names to apply to the component */
    className: PropTypes.string,

    /** Whether this is a CARFAX tile (affects conditional rendering with removeVhrCarfaxTile) */
    isCarfaxTile: PropTypes.bool,

    /** Whether to use inverse styling (light text on dark background) */
    isInverse: PropTypes.bool,

    /** Whether to show email lead gate instead of direct link */
    isLeadGateEnabled: PropTypes.bool,

    /** Email CTA label key for lead gate rendering (e.g., 'carfaxAppended', 'experianAppended') */
    labelKey: PropTypes.string,

    /** Text to display in the link */
    linkText: PropTypes.string,

    /** The id of the listing*/
    listingId: PropTypes.string,

    /** Click handler for the link */
    onClick: PropTypes.func,

    /** Click handler for email CTA lead gate */
    onEmailCtaClick: PropTypes.func,

    /** Whether to remove CARFAX tile props (experiment flag) */
    removeVhrCarfaxTile: PropTypes.bool,

    /** VHR tile data containing link information and other tile properties */
    vhrTile: PropTypes.object,
};
